import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Briefcase, Plus, Trash2, Edit3, Sparkles, Loader2, MapPin, Clock, X, Save, Eye, EyeOff,
} from "lucide-react";
import {
    adminFetchJobs, adminCreateJob, adminUpdateJob, adminDeleteJob, adminGenerateJD,
} from "@/lib/admin";

const EMPTY = { title: "", location: "", experience: "", type: "Full-time", description: "", active: true };

function JobForm({ initial, onCancel, onSave }) {
    const [data, setData] = useState({ ...EMPTY, ...(initial || {}) });
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);

    const onChange = (e) => setData((d) => ({ ...d, [e.target.name]: e.target.value }));

    const onGenerate = async () => {
        if (!data.title || !data.experience) {
            toast.error("Please enter Title and Experience first.");
            return;
        }
        setGenerating(true);
        try {
            const res = await adminGenerateJD({
                title: data.title,
                experience: data.experience,
                location: data.location,
                type: data.type,
            });
            setData((d) => ({ ...d, description: res.description }));
            toast.success("Description generated by Gemini Flash.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not generate description.");
        } finally {
            setGenerating(false);
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const saved = initial?.id
                ? await adminUpdateJob(initial.id, data)
                : await adminCreateJob(data);
            toast.success(initial?.id ? "Job updated." : "Job posted.");
            onSave(saved);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={onSubmit} data-testid="job-form" className="bg-white border-2 border-[#28406A] p-5 sm:p-7">
            <div className="flex items-center justify-between mb-5">
                <p className="font-display uppercase text-base sm:text-lg text-[#101A2C]">
                    {initial?.id ? "Edit Job Opening" : "Post New Job Opening"}
                </p>
                <button type="button" onClick={onCancel} data-testid="job-form-close" className="p-1 text-[#4B6390] hover:text-[#28406A]">
                    <X className="h-5 w-5" />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="label-vl">Job Title *</label>
                    <input data-testid="job-title" name="title" required value={data.title} onChange={onChange} className="input-vl" placeholder="e.g. Senior Logistics Coordinator" />
                </div>
                <div>
                    <label className="label-vl">Experience *</label>
                    <input data-testid="job-experience" name="experience" required value={data.experience} onChange={onChange} className="input-vl" placeholder="e.g. 3-5 years" />
                </div>
                <div>
                    <label className="label-vl">Location *</label>
                    <input data-testid="job-location" name="location" required value={data.location} onChange={onChange} className="input-vl" placeholder="e.g. Waluj, Aurangabad" />
                </div>
                <div>
                    <label className="label-vl">Employment Type</label>
                    <select data-testid="job-type" name="type" value={data.type} onChange={onChange} className="input-vl">
                        {["Full-time", "Part-time", "Contract", "Internship"].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
            </div>

            <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                    <label className="label-vl mb-0">Description *</label>
                    <button
                        type="button"
                        onClick={onGenerate}
                        disabled={generating}
                        data-testid="job-ai-generate"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#28406A] text-white text-[10px] uppercase tracking-widest font-bold hover:bg-[#101A2C] disabled:opacity-60"
                    >
                        {generating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        {generating ? "Generating…" : "AI Generate"}
                    </button>
                </div>
                <textarea
                    data-testid="job-description"
                    name="description"
                    required
                    rows={10}
                    value={data.description}
                    onChange={onChange}
                    className="input-vl font-mono text-xs leading-relaxed"
                    placeholder="Write a job description or click AI Generate to auto-create one from the title and experience."
                />
                <p className="text-[10px] text-[#4B6390] mt-2">
                    <Sparkles className="h-3 w-3 inline-block mr-1 text-[#F4A000]" />
                    AI generation uses Gemini 3 Flash — tailored to logistics, automotive & manufacturing.
                </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="submit"
                    disabled={saving}
                    data-testid="job-save"
                    className="btn-primary disabled:opacity-60"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {initial?.id ? "Update Job" : "Post Job"}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    data-testid="job-cancel"
                    className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-bold text-[#4B6390] border border-[#CBD5E1] hover:border-[#28406A] hover:text-[#28406A]"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

export default function JobsManager() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const data = await adminFetchJobs();
            setJobs(data);
        } catch {
            toast.error("Could not load jobs.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const onDelete = async (id) => {
        if (!window.confirm("Delete this job posting permanently?")) return;
        try {
            await adminDeleteJob(id);
            setJobs((js) => js.filter((j) => j.id !== id));
            toast.success("Job deleted.");
        } catch {
            toast.error("Could not delete.");
        }
    };

    const onToggleActive = async (job) => {
        try {
            const updated = await adminUpdateJob(job.id, { active: !job.active });
            setJobs((js) => js.map((j) => (j.id === job.id ? updated : j)));
            toast.success(updated.active ? "Job is now live." : "Job hidden from careers page.");
        } catch {
            toast.error("Could not update.");
        }
    };

    const onSaved = (saved) => {
        setJobs((js) => {
            const exists = js.some((j) => j.id === saved.id);
            return exists ? js.map((j) => (j.id === saved.id ? saved : j)) : [saved, ...js];
        });
        setShowForm(false);
        setEditing(null);
    };

    return (
        <div data-testid="jobs-manager">
            {/* Toolbar */}
            <div className="bg-white border border-[#CBD5E1] p-4 mb-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                    <span className="w-9 h-9 inline-flex items-center justify-center bg-[#28406A] text-white">
                        <Briefcase className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <div>
                        <p className="font-display uppercase text-sm text-[#101A2C]">Job Openings</p>
                        <p className="text-[10px] uppercase tracking-widest text-[#4B6390]">{jobs.length} posting{jobs.length === 1 ? "" : "s"}</p>
                    </div>
                </div>
                <button
                    onClick={() => { setEditing(null); setShowForm(true); }}
                    data-testid="new-job-btn"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#F4A000] text-[#101A2C] text-xs uppercase tracking-widest font-bold hover:bg-[#FBC55A]"
                >
                    <Plus className="h-4 w-4" /> New Job
                </button>
            </div>

            {/* Form */}
            {showForm && (
                <div className="mb-6">
                    <JobForm
                        initial={editing}
                        onCancel={() => { setShowForm(false); setEditing(null); }}
                        onSave={onSaved}
                    />
                </div>
            )}

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {loading && (
                    <div className="md:col-span-2 bg-white border border-[#CBD5E1] p-10 text-center text-[#4B6390]">
                        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading jobs…
                    </div>
                )}
                {!loading && jobs.length === 0 && (
                    <div className="md:col-span-2 bg-white border border-[#CBD5E1] p-10 text-center text-[#4B6390]" data-testid="jobs-empty">
                        No job openings yet. Click <strong>New Job</strong> to post one.
                    </div>
                )}
                {!loading && jobs.map((j) => (
                    <div key={j.id} data-testid={`job-card-${j.id}`} className="bg-white border border-[#CBD5E1] p-5 sm:p-6 hover:border-[#28406A] transition-colors">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-widest text-[#4B6390]">{j.type}</p>
                                <h3 className="font-display uppercase text-base sm:text-lg text-[#101A2C] mt-1">{j.title}</h3>
                            </div>
                            <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-widest font-bold ${j.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                                {j.active ? "Live" : "Hidden"}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-[11px] text-[#4B6390] mb-3">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>
                            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{j.experience}</span>
                        </div>
                        <p className="text-xs text-[#4B6390] leading-relaxed line-clamp-3 mb-4 whitespace-pre-line">{j.description}</p>
                        <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-[#CBD5E1]">
                            <button
                                onClick={() => { setEditing(j); setShowForm(true); }}
                                data-testid={`edit-job-${j.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[#28406A] border border-[#CBD5E1] hover:border-[#28406A]"
                            >
                                <Edit3 className="h-3 w-3" /> Edit
                            </button>
                            <button
                                onClick={() => onToggleActive(j)}
                                data-testid={`toggle-job-${j.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-[#4B6390] border border-[#CBD5E1] hover:border-[#28406A] hover:text-[#28406A]"
                            >
                                {j.active ? <><EyeOff className="h-3 w-3" /> Hide</> : <><Eye className="h-3 w-3" /> Publish</>}
                            </button>
                            <button
                                onClick={() => onDelete(j.id)}
                                data-testid={`delete-job-${j.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-red-700 border border-red-200 hover:bg-red-50 ml-auto"
                            >
                                <Trash2 className="h-3 w-3" /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
